import type { SledTeam } from '@sdd-maturity/shared-types';
import { useApi } from './useApi';
import { resolveNodeByNumber } from '../lib/hierarchy-route';

export interface ScopeNodeResult {
  /** The URL-resolved scope node, or null while loading / not found. */
  node: SledTeam | null;
  /** The full cached hierarchy tree (used for parent-cascade backfill). */
  teams: SledTeam[];
  isLoading: boolean;
  needsAuth: boolean;
  error: string | null;
  /** Tree loaded successfully but the `number` matched no node. */
  notFound: boolean;
  refetch: () => void;
}

/**
 * AC-1.2: resolve the active scope node from a URL `:number` against the cached
 * `getAllTeams()` tree — no dependence on `sessionStorage`/`TeamContext`.
 * `expectedTier` (derived from the typed URL segment) makes resolution tier-aware
 * so a cross-tier number collision can't mis-render (see {@link resolveNodeByNumber}).
 */
export function useScopeNode(number?: string, expectedTier?: number): ScopeNodeResult {
  const { data, isLoading, needsAuth, error, refetch } = useApi<{ teams: SledTeam[] }>(
    (client, token) => client.getAllTeams(token) as Promise<{ teams: SledTeam[] }>,
    [],
  );

  const teams = data?.teams ?? [];
  const node = number ? resolveNodeByNumber(teams, number, expectedTier) : null;
  const notFound = !isLoading && !needsAuth && !error && teams.length > 0 && node === null;

  return { node, teams, isLoading, needsAuth, error, notFound, refetch };
}
